import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Linkedin, ArrowUpRight, Check } from "lucide-react";
import { WhatsAppIcon, WHATSAPP_URL } from "@/components/WhatsAppIcon";
import ScrollReveal from "@/components/ScrollReveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const STAGES = [
  "Idea / Concept",
  "Feasibility",
  "Engineering",
  "Commissioning",
  "Operating & Scaling",
];

const REASONS = [
  { value: "Project / Partnership Inquiry", subject: "Project Inquiry" },
  { value: "Technology Inquiry", subject: "Technology Inquiry" },
  { value: "Career Inquiry", subject: "Career Inquiry" },
  { value: "Media / Press", subject: "Media Inquiry" },
  { value: "Other", subject: "General Inquiry" },
];

const CONTACT_EMAIL = "info@tanrentech.com";

const Contact = () => {
  usePageMeta(
    "Contact Tanren — Start Your Project",
    "Talk to Tanren about your biomass project: pyrolysis, torrefaction, gasification, CBG or MRV."
  );
  const [searchParams] = useSearchParams();
  const preReason = searchParams.get("reason") || "";
  const isValidReason = REASONS.some((r) => r.value === preReason);

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    reason: isValidReason ? preReason : "",
    stage: STAGES[0],
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({});

  // Sync form.reason if the URL ?reason=... changes after mount (e.g. client-side navigation)
  useEffect(() => {
    if (isValidReason && preReason !== form.reason) {
      setForm((f) => ({ ...f, reason: preReason }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preReason]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";
    if (!form.email.trim()) errs.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email.";
    if (!form.reason) errs.reason = "Please select a reason for contact.";
    if (!form.message.trim()) errs.message = "Tell us a bit about your inquiry.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        let detail = "";
        try {
          const data = await res.json();
          detail = data?.detail || "";
        } catch (_) {}
        throw new Error(detail || `Request failed (${res.status})`);
      }
      setSent(true);
    } catch (err) {
      setSubmitError(
        "We couldn't send your message just now. Please try again, or email us directly at " +
          CONTACT_EMAIL +
          "."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "field";

  const labelClass = "mono-label block mb-2";

  return (
    <div data-testid="page-contact">
      <section className="bg-paper text-char-900 pt-40 pb-16 border-b border-char-900/10">
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <h1 className="font-display mt-6 max-w-[22ch] text-char-900">
            Tell us about your feedstock, your site, or your stage.
          </h1>
        </div>
      </section>

      <section className="bg-paper py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-14">
          <ScrollReveal className="md:col-span-5">
            <p className="text-lg text-char-700 leading-relaxed">
              Whether you have a bag of biomass, a site, a pilot plant, or an operating facility that
              needs to hit its yield targets — we'll tell you what's possible, what it takes, and
              whether Tanren is the right partner.
            </p>

            <div className="mt-12 space-y-6">
              <div>
                <div className={labelClass}>Direct email</div>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="contact-direct-email"
                  className="mt-2 inline-flex items-center gap-2 font-display text-2xl md:text-3xl tracking-tight text-char-900 hover:text-ember transition-colors"
                >
                  {CONTACT_EMAIL}
                  <ArrowUpRight size={22} />
                </a>
              </div>
              <div>
                <div className={labelClass}>Social</div>
                <div className="mt-2 flex items-center gap-3">
                  <a
                    href="https://www.linkedin.com/company/tanrentech"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-linkedin"
                    aria-label="LinkedIn"
                    className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-char-900/15 hover:border-char-900 text-char-700 hover:text-char-900 transition-colors"
                  >
                    <Linkedin size={16} />
                  </a>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp"
                    aria-label="Chat on WhatsApp"
                    className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-char-900/15 hover:border-char-900 text-char-700 hover:text-char-900 transition-colors"
                  >
                    <WhatsAppIcon size={16} />
                  </a>
                </div>
              </div>
              <div>
                <div className={labelClass}>Office</div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Regus%2C%20Platinum%20Techno%20Park%2C%20Sector%2030%2C%20Vashi%2C%20Navi%20Mumbai%2C%20Maharashtra%20400705"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-address"
                  className="mt-2 block text-char-900 text-base leading-relaxed max-w-[38ch] hover:text-ember transition-colors"
                >
                  Level 13, Regus, Platinum Techno Park, Sector 30, Vashi, Navi Mumbai, Maharashtra
                  400705, India
                </a>
              </div>
              <div>
                <div className={labelClass}>Response time</div>
                <div className="mt-2 text-char-900 text-base">Typically within 2 business days.</div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120} className="md:col-span-7">
            {sent ? (
              <div
                data-testid="contact-success"
                className="border border-char-900/10 bg-paper p-8 lg:p-10"
              >
                <div className="tick" aria-hidden="true">
                  <Check size={14} strokeWidth={3} />
                </div>
                <h2 className="mt-6 font-display text-char-900">
                  Thanks — we've received your message.
                </h2>
                <p className="mt-3 text-char-700 leading-relaxed max-w-[52ch]">
                  We'll be in touch within two business days at the email you provided. In the
                  meantime, if you have anything to add, you can reach us directly at{" "}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-ember underline underline-offset-4">
                    {CONTACT_EMAIL}
                  </a>
                  .
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another"
                  className="mt-8 btn-ghost-light"
                  onClick={() => {
                    setSent(false);
                    setForm((f) => ({ ...f, message: "" }));
                  }}
                >
                  Send another
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="border border-char-900/10 p-8 lg:p-10 bg-paper"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="cf-name" className={labelClass}>
                      Full Name *
                    </label>
                    <input
                      id="cf-name"
                      data-testid="contact-input-name"
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      className={inputClass}
                      placeholder="Your name"
                    />
                    {errors.name && (
                      <div className="mt-2 text-sm text-ember">{errors.name}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="cf-company" className={labelClass}>
                      Company
                    </label>
                    <input
                      id="cf-company"
                      data-testid="contact-input-company"
                      type="text"
                      value={form.company}
                      onChange={update("company")}
                      className={inputClass}
                      placeholder="Optional"
                    />
                  </div>

                  <div>
                    <label htmlFor="cf-email" className={labelClass}>
                      Email *
                    </label>
                    <input
                      id="cf-email"
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className={inputClass}
                      placeholder="you@company.com"
                    />
                    {errors.email && (
                      <div className="mt-2 text-sm text-ember">{errors.email}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="cf-phone" className={labelClass}>
                      Phone
                    </label>
                    <input
                      id="cf-phone"
                      data-testid="contact-input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      className={inputClass}
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="cf-reason" className={labelClass}>
                      Reason for Contact *
                    </label>
                    <select
                      id="cf-reason"
                      data-testid="contact-input-reason"
                      value={form.reason}
                      onChange={update("reason")}
                      className={`${inputClass} appearance-none cursor-pointer`}
                    >
                      <option value="" disabled>
                        Select an option
                      </option>
                      {REASONS.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.value}
                        </option>
                      ))}
                    </select>
                    {errors.reason && (
                      <div className="mt-2 text-sm text-ember" data-testid="contact-error-reason">
                        {errors.reason}
                      </div>
                    )}
                    {form.reason === "Career Inquiry" && (
                      <div
                        className="mt-3 text-sm text-char-700 leading-relaxed"
                        data-testid="contact-career-helper"
                      >
                        Tell us about the role you're interested in. If you'd like to share a
                        resume, email it directly to{" "}
                        <a
                          href={`mailto:${CONTACT_EMAIL}`}
                          className="text-ember underline underline-offset-4"
                        >
                          {CONTACT_EMAIL}
                        </a>
                        .
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="cf-stage" className={labelClass}>
                      Project Stage (if applicable)
                    </label>
                    <select
                      id="cf-stage"
                      data-testid="contact-input-stage"
                      value={form.stage}
                      onChange={update("stage")}
                      className={`${inputClass} appearance-none cursor-pointer`}
                    >
                      {STAGES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="cf-message" className={labelClass}>
                      Message *
                    </label>
                    <textarea
                      id="cf-message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      rows={5}
                      className={`${inputClass} resize-none`}
                      placeholder="Feedstock, site, project stage, goals — as much detail as you can."
                    />
                    {errors.message && (
                      <div className="mt-2 text-sm text-ember">{errors.message}</div>
                    )}
                  </div>
                </div>

                {/* Honeypot — hidden from humans, visible/fillable to naive bots. */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="cf-website">Website</label>
                  <input
                    id="cf-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                  />
                </div>

                {submitError && (
                  <div
                    data-testid="contact-submit-error"
                    className="mt-8 text-sm text-ember"
                    role="alert"
                  >
                    {submitError}
                  </div>
                )}

                <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <p className="text-sm text-char-700 max-w-[42ch]">
                    We'll receive your message directly and reply within two business days.
                  </p>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="btn-ember justify-center md:justify-start disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending…" : "Send Enquiry"}
                    <ArrowUpRight size={18} />
                  </button>
                </div>
              </form>
            )}
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
